"use client";

import { useState } from "react";
import { FiCopy } from "react-icons/fi";
import { LuMail, LuMessageSquare } from "react-icons/lu";
import type { EmailTopLevelNode } from "@/components/email-composer/types/tiptap-json";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTenant } from "@/contexts/tenant-provider";
import { useSendConnectLink } from "@/hooks/use-send-connect-link";
import { toast } from "@/lib/use-toast";
import { buildConnectLinkEmailBody } from "@/lib/wearable-connect-email";
import type { PatientSummary } from "@/types/patient";
type Channel = "text" | "email";
interface SendConnectLinkDialogProps {
  patient: PatientSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Hands the drafted body to whoever renders the composer.
   *
   * The composer cannot live here. Closing this dialog is what the email path
   * does next, and the caller mounts this component conditionally on the same
   * `open` — so a drawer rendered here would be unmounted by the very close
   * that is meant to reveal it, minting a live link and dropping it. The owner
   * of the composer has to outlive the dialog, which means it is the caller.
   */
  onComposeEmail: (bodyNodes: EmailTopLevelNode[]) => void;
}

/**
 * Drafting a patient the link to connect a device on their own phone.
 *
 * Drafts, never sends — both channels end with a message in front of staff,
 * matching the forms dialog next to it. No messaging-permission gate, for the
 * same reason: neither the forms dialog nor the quote share checks one, and a
 * third behaviour here would only make the three disagree.
 *
 * The link is a bearer credential for as long as the aggregator honours it —
 * whoever opens it connects THEIR device onto this patient's chart — so the
 * recipient is worth reading before sending, which is the whole argument for
 * drafting rather than sending.
 */
export default function SendConnectLinkDialog({
  patient,
  open,
  onOpenChange,
  onComposeEmail
}: SendConnectLinkDialogProps) {
  const {
    tenant
  } = useTenant();
  const {
    createConnectLink,
    sendConnectLinkByText,
    isSending
  } = useSendConnectLink();
  const [channel, setChannel] = useState<Channel>("text");
  // A minted link the chat could not take. Shown rather than dropped: the
  // session exists at the aggregator either way, and this is the only copy of it.
  const [strandedUrl, setStrandedUrl] = useState<string | null>(null);
  const [isPreparingEmail, setIsPreparingEmail] = useState(false);
  const noPhone = channel === "text" && !patient.phoneNumber;
  const noEmail = channel === "email" && !patient.email;
  const close = () => {
    setStrandedUrl(null);
    onOpenChange(false);
  };
  const handleSendText = async () => {
    setStrandedUrl(null);
    try {
      const {
        session,
        chatId
      } = await sendConnectLinkByText(patient);
      if (chatId) {
        close();
        return;
      }
      // `openChat` has already said what went wrong; this says what to do about
      // it, and holds the dialog open so the link stays reachable.
      setStrandedUrl(session.url);
    } catch {
      // Minting failed and nothing was created — the hook has toasted.
    }
  };
  const handleComposeEmail = async () => {
    setStrandedUrl(null);
    setIsPreparingEmail(true);
    try {
      const session_0 = await createConnectLink(patient.id);
      onComposeEmail(buildConnectLinkEmailBody(patient.firstName, session_0.url, tenant?.name, tenant?.brandPrimaryColor ?? "#4a5d4e"));
      onOpenChange(false);
    } catch {
      // The hook has toasted; nothing was minted.
    } finally {
      setIsPreparingEmail(false);
    }
  };
  const handleCopy = async () => {
    if (!strandedUrl) return;
    try {
      await navigator.clipboard.writeText(strandedUrl);
      toast({
        title: "Link copied",
        variant: "success"
      });
    } catch {
      toast({
        title: "Couldn't copy the link",
        variant: "error"
      });
    }
  };
  return <Dialog open={open} onOpenChange={next => next ? null : close()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send a connect link</DialogTitle>
          <p className="text-body text-foreground-secondary">
            Send {patient.firstName} a link to connect a device on their own
            phone. Their readings land on this chart either way.
          </p>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            <Tabs value={channel} onValueChange={value => setChannel(value as Channel)}>
              <TabsList variant="solid" className="w-full">
                <TabsTrigger value="text" className="flex-1 gap-1.5">
                  <LuMessageSquare size={14} />
                  SMS
                </TabsTrigger>
                <TabsTrigger value="email" className="flex-1 gap-1.5">
                  <LuMail size={14} />
                  Email
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {noPhone && <p className="text-body text-status-warning-text">
                This patient does not have a phone number on file.
              </p>}

            {noEmail && <p className="text-body text-status-warning-text">
                This patient does not have an email address on file.
              </p>}

            <p className="text-body text-foreground-secondary">
              Anyone who opens the link can connect a device to this chart, so
              check who you are sending it to. Readings can take up to a day to
              appear — press Sync to pull them in sooner.
            </p>

            {strandedUrl && <div className="space-y-2 rounded-md border border-border bg-background-secondary p-3">
                <p className="text-body text-foreground">
                  The chat couldn&apos;t be opened, so here is the link. Copy it
                  before closing — it is not stored anywhere.
                </p>
                <p className="break-all text-caption text-foreground-secondary">
                  {strandedUrl}
                </p>
                <Button size="sm" variant="primary" onClick={handleCopy}>
                  <FiCopy className="mr-1 h-3 w-3" />
                  Copy link
                </Button>
              </div>}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          {channel === "text" ? <Button variant="secondary" onClick={handleSendText} disabled={isSending || noPhone} isLoading={isSending}>
              Open Chat
            </Button> : <Button variant="secondary" onClick={handleComposeEmail} disabled={isPreparingEmail || noEmail} isLoading={isPreparingEmail}>
              Compose Email
            </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}